import moment from 'moment-timezone'
import { generateApiActionTypes } from 'utils/actions/generateActionTypes'
import { ENDPOINTS } from 'utils/api/callApi'
import {
  ISpotBiddingRule,
  ISpotBiddingConfig,
} from 'apps/spot-quote-control-panel/types/spotBiddingConfig'

export const GET_SPOT_BIDDING_CONFIG_ACTION_TYPES = generateApiActionTypes(
  'GET_SPOT_BIDDING_CONFIG'
)
export const CREATE_SPOT_BIDDING_CONFIG_ACTION_TYPES = generateApiActionTypes(
  'CREATE_SPOT_BIDDING_CONFIG'
)
export const UPDATE_SPOT_BIDDING_CONFIG_ACTION_TYPES = generateApiActionTypes(
  'UPDATE_SPOT_BIDDING_CONFIG'
)
export const DELETE_SPOT_BIDDING_CONFIG_ACTION_TYPES = generateApiActionTypes(
  'DELETE_SPOT_BIDDING_CONFIG'
)
export const CREATE_SPOT_BIDDING_RULE_ACTION_TYPES = generateApiActionTypes(
  'CREATE_SPOT_BIDDING_RULE'
)
export const UPDATE_SPOT_BIDDING_RULE_ACTION_TYPES = generateApiActionTypes(
  'UPDATE_SPOT_BIDDING_RULE'
)
export const DELETE_SPOT_BIDDING_RULE_ACTION_TYPES = generateApiActionTypes(
  'DELETE_SPOT_BIDDING_RULE'
)

export const getSpotBiddingConfig = (id?: string) => ({
  payload: {
    callApi: true,
    requestData: {
      endpoint: ENDPOINTS.spotBiddingConfigs,
      method: 'GET',
      id,
    },
    actionTypes: Object.keys(GET_SPOT_BIDDING_CONFIG_ACTION_TYPES),
  },
  type: '',
})

export const getGlobalConfig = () => ({
  payload: {
    callApi: true,
    requestData: {
      endpoint: ENDPOINTS.globalSpotConfig,
      method: 'GET',
    },
    actionTypes: Object.keys(GET_SPOT_BIDDING_CONFIG_ACTION_TYPES),
  },
  type: '',
})

export const createSpotBiddingConfig = (config: Partial<ISpotBiddingConfig>) => {
  const { shipperApplications = [] } = config
  const shipperApplicationIds = shipperApplications.map(shipper => shipper?.id)
  return {
    payload: {
      callApi: true,
      requestData: {
        endpoint: ENDPOINTS.spotBiddingConfigs,
        method: 'POST',
        body: {
          spotBiddingConfig: {
            ...config,
            shipperApplicationIds,
            spotBiddingRules: undefined,
          },
        },
      },
      actionTypes: Object.keys(CREATE_SPOT_BIDDING_CONFIG_ACTION_TYPES),
    },
    type: '',
  }
}

export const updateSpotBiddingConfig = (config: Partial<ISpotBiddingConfig>) => {
  const { id, shipperApplications = [] } = config || {}
  const shipperApplicationIds = shipperApplications.map(shipper => shipper?.id)
  return {
    payload: {
      callApi: true,
      requestData: {
        endpoint: ENDPOINTS.spotBiddingConfigs,
        id,
        method: 'PUT',
        body: {
          spotBiddingConfig: {
            ...config,
            shipperApplicationIds,
            spotBiddingRules: undefined,
          },
        },
      },
      actionTypes: Object.keys(UPDATE_SPOT_BIDDING_CONFIG_ACTION_TYPES),
    },
    type: '',
  }
}

export const deleteSpotBiddingConfig = (configId: string) => ({
  payload: {
    callApi: true,
    requestData: {
      endpoint: ENDPOINTS.spotBiddingConfigs,
      id: configId,
      method: 'DELETE',
    },
    actionTypes: Object.keys(DELETE_SPOT_BIDDING_CONFIG_ACTION_TYPES),
  },
  type: '',
})

const spotBiddingRulePayload = (
  spotBiddingConfig: ISpotBiddingConfig,
  spotBiddingRule: RecursivePartial<ISpotBiddingRule>
) => {
  const { id: spotBiddingConfigId, shipperApplication } = spotBiddingConfig || {}
  const { id: shipperApplicationId } = shipperApplication || {}

  return {
    spotBiddingConfigId,
    shipperApplicationId,
    ...spotBiddingRule,
  }
}

export const createSpotBiddingRule = (
  spotBiddingConfig: ISpotBiddingConfig,
  spotBiddingRule: RecursivePartial<ISpotBiddingRule>
) => {
  const payload = spotBiddingRulePayload(spotBiddingConfig, spotBiddingRule)
  return {
    payload: {
      callApi: true,
      requestData: {
        endpoint: ENDPOINTS.spotBiddingRules,
        method: 'POST',
        body: {
          spotBiddingRule: payload,
        },
      },
      actionTypes: Object.keys(CREATE_SPOT_BIDDING_RULE_ACTION_TYPES),
    },
    type: '',
  }
}

export const updateSpotBiddingRule = (
  spotBiddingConfig: ISpotBiddingConfig,
  spotBiddingRule: RecursivePartial<ISpotBiddingRule>
) => {
  const payload = spotBiddingRulePayload(spotBiddingConfig, spotBiddingRule)

  return {
    payload: {
      callApi: true,
      requestData: {
        endpoint: ENDPOINTS.spotBiddingRules,
        method: 'PUT',
        id: spotBiddingRule?.id,
        body: {
          spotBiddingRule: {
            ...payload,
          },
        },
      },
      actionTypes: Object.keys(UPDATE_SPOT_BIDDING_RULE_ACTION_TYPES),
    },
    type: '',
  }
}

export const deleteSpotBiddingRule = (ruleId?: string) => ({
  payload: {
    callApi: true,
    requestData: {
      endpoint: ENDPOINTS.spotBiddingRules,
      method: 'PUT',
      id: ruleId,
      body: {
        spotBiddingRule: {
          deletedAt: moment().toISOString(),
        },
      },
    },
    actionTypes: Object.keys(DELETE_SPOT_BIDDING_RULE_ACTION_TYPES),
  },
  type: '',
})

export enum ADD_SPOT_BIDDING_RULE_MODAL {
  ADD_SPOT_BIDDING_RULE_MODAL_OPEN = 'ADD_SPOT_BIDDING_RULE_MODAL_OPEN',
  ADD_SPOT_BIDDING_RULE_MODAL_CLOSE = 'ADD_SPOT_BIDDING_RULE_MODAL_CLOSE',
}

export const openAddSpotBiddingRuleModal = (rule?: ISpotBiddingRule) => ({
  type: ADD_SPOT_BIDDING_RULE_MODAL.ADD_SPOT_BIDDING_RULE_MODAL_OPEN,
  payload: { rule },
})

export const closeAddSpotBiddingRuleModal = () => ({
  type: ADD_SPOT_BIDDING_RULE_MODAL.ADD_SPOT_BIDDING_RULE_MODAL_CLOSE,
})

export enum DELETE_SPOT_BIDDING_RULE_MODAL {
  DELETE_SPOT_BIDDING_RULE_MODAL_OPEN = 'DELETE_SPOT_BIDDING_RULE_MODAL_OPEN',
  DELETE_SPOT_BIDDING_RULE_MODAL_CLOSE = 'DELETE_SPOT_BIDDING_RULE_MODAL_CLOSE',
}

export const openDeleteSpotBiddingRuleModal = (rule: ISpotBiddingRule) => ({
  type: DELETE_SPOT_BIDDING_RULE_MODAL.DELETE_SPOT_BIDDING_RULE_MODAL_OPEN,
  payload: { rule },
})
export const closeDeleteSpotBiddingRuleModal = () => ({
  type: DELETE_SPOT_BIDDING_RULE_MODAL.DELETE_SPOT_BIDDING_RULE_MODAL_CLOSE,
})
